<template>
  <div :class="['subscribe-popup', { 'minified': !isOpen }]">
    <div class="left-content">
      <div
        class="minified-content"
        @click="togglePopup()"
      >
        <button
          class="expand-button"
          :aria-label="$t('subscribe.popup.title')"
        />
        <div class="title">{{ $t('subscribe.popup.title') }}</div>
      </div>
    </div>

    <template v-if="isOpen">
      <div class="right-content">
        <template v-if="!isSuccess">
          <div class="title">{{ $t('subscribe.popup.title') }}</div>
          <div class="description">{{ $t('subscribe.popup.description') }}</div>
          <subscribe-form
            :is-popup="true"
            :callback="onSuccesCallback"
            :subscribe-source="subscribeSource"
          />
          <span
            v-dompurify-html="$t('privacyPolicyAgreement.subscribe')"
            class="privacy-policy-agreement"
          />
        </template>

        <div
          v-else
          class="subscribe-popup-success"
        >
          <div class="title">{{ $t('subscribe.popup.success.title') }}</div>
          <div class="description">{{ $t('subscribe.popup.success.description') }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>

import CookieUtils from 'utils/CookieUtils'
import UrlParams from 'utils/UrlParams'

export default {
  name: "SubscribePopup",
  components: {
    SubscribeForm: () => import('components/Subscribe/SubscribeForm')
  },
  props: {
    closedPopupCookieName: {
      type: String,
      required: true
    },
    viewedPopupCookieName: {
      type: String,
      required: true
    },
    subscribedPopupCookieName: {
      type: String,
      required: true
    },
    subscribeSource: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isOpen: true,
      isSuccess: false,
      oneYear: 365
    }
  },
  created () {
    const viewed = CookieUtils.getCookie(this.viewedPopupCookieName)
    const isClosed = CookieUtils.getCookie(this.closedPopupCookieName)

    if (isClosed === '') {
      CookieUtils.setCookie(this.closedPopupCookieName, 0, this.oneYear)
    }

    const value = viewed === '' ? 1 : parseInt(viewed)+1;

    CookieUtils.setCookie(this.viewedPopupCookieName, value, this.oneYear)
  },
  mounted () {
    const isClosed = parseInt(CookieUtils.getCookie(this.closedPopupCookieName))
    const isViewed = parseInt(CookieUtils.getCookie(this.viewedPopupCookieName))
    const tab = UrlParams.getUrlParam(window.location.href, 'opentab')
    this.isOpen = !isClosed && isViewed < 10000 && tab === null
  },
  methods: {
    togglePopup () {
      if (this.isOpen) {
        this.minifyPopup()
      } else {
        this.isOpen = true
      }
      this.doSubscribePopupDataLayerPush()
    },

    minifyPopup () {
      this.isOpen = false
      CookieUtils.setCookie(this.closedPopupCookieName, 1, this.oneYear)

      if (this.isSuccess) {
        this.$emit('subscribed')
      }
    },
    onSuccesCallback () {
      this.isSuccess = true
      CookieUtils.setCookie(this.subscribedPopupCookieName, 1, this.oneYear)
    },

    doSubscribePopupDataLayerPush() {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'subscribeEventPopup',
        eventCategory: 'Newsletter',
        eventAction: 'Newsletter toggle popup',
        eventLabel: 'Newsletter index-popup',
        eventValue: this.isOpen ? 1 : 0
      })
    },
  }
}
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .subscribe-popup {
    display: flex;
    z-index: 3;
    position: fixed;
    right: 34px;
    top: 285px;
    border-radius: 10px;
    transition: 0.4s ease;
    color: white;
    background-color: $blue-bg-css;
    box-shadow: 0 2px 8px 0 rgba(21,38,73,0.2);

    &.minified {
      right: 0;
      min-height: 340px;
      width: 48px;
      border-radius: 10px 0 0 10px;
      background: $blue-dark;

      @include bp-desktop-small {
        top: 45%;
      }

      .expand-button {

        &::after {
          width: 8px;
          transform: rotate(180deg);
        }
      }
    }
  }

  .expand-button {
    width: 32px;
    height: 32px;
    border-radius: 21.5px;
    background-color: #cdda32;

    &::after {
      content: $icon-arrow-right-thin-medium;
      @include icon-font-thin();
      font-size: em(11);
      line-height: 30px;
      font-weight: bold;
      color: $blue-dark;
    }
  }

  .left-content {
    min-height: 340px;
    padding: 12px 8px;
    cursor: pointer;
    position: relative;
    border-radius: 6px 0 0 6px;
    background: linear-gradient(90deg, #254E83 0%, #10264C 100%);

    .title {
      position: absolute;
      bottom: 0;
      left: 24px;
      min-width: 280px;
      transform: rotate(270deg);
      transform-origin: left;
      line-height: 32px;
      font-size: 16px;
      font-weight: 700;
      text-align: center;
    }
  }

  .right-content {
    width: 360px;
    min-height: 320px;
    padding: 32px;
    color: $blue-dark;
    text-align: center;

    .title {
      margin-bottom: 15px;
      font-size: em(20);
      font-weight: 500;
      line-height: 22px;
    }

    .description {
      font-size: em(14);
      line-height: 20px;
    }

    .privacy-policy-agreement {
      margin-top: 16px;
      color: $text-secondary;
      font-size: em(12);

      ::v-deep a {
        color: $blue-medium;
        text-decoration: underline;
      }
    }

    .subscribe-popup-success {
      padding-top: 32px;

      &::before {
        display: block;
        background: white;
        width: 88px;
        height: 88px;
        border-radius: 44px;
        line-height: 88px;
        @include icon-font-thin();
        content: $icon-css-check;
        font-size: em(32);
        color: #D1DD39;
      }

      .title {
        margin-top: 28px;
      }
    }
  }
</style>
